import { CheckIcon } from '@heroicons/react/outline';
import Image from 'next/image';
import { useRouter } from 'next/router';
import useLocale from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import clsxm from '@/lib/clsxm';

import { HEADSHOTS_PRICES } from '@/constants/common';
import { useAnalytics } from '@/contexts/Analytics';
import { postData } from '@/utils/helpers';
import { getStripe } from '@/utils/stripe-client';
import { useUser } from '@/utils/useUser';

import StudioPricing from './landing/StudioPricing';
import Button from '../buttons/Button';

export default function PricingSection({
  appearance = 'default',
}: {
  appearance?: 'default' | 'studio';
}) {
  const { t } = useLocale('ai-headshot-generator');
  const [pricingType, setPricingType] = useState<'individual' | 'team'>(
    'individual',
  );
  const [teamSize, setTeamSize] = useState(5);
  const [pendingPrice, setPendingPrice] = useState<string | null>(null);

  const router = useRouter();
  const analytics = useAnalytics();
  const { user } = useUser();

  // Calculate discount based on team size
  const { discount, discountedPrice, originalPrice } = useMemo(() => {
    let discountPercent = 0;

    if (teamSize >= 30) {
      discountPercent = 30;
    } else if (teamSize >= 20) {
      discountPercent = 20;
    } else if (teamSize >= 10) {
      discountPercent = 10;
    }

    const basePrice = teamSize * 29;
    const discountAmount = basePrice * (discountPercent / 100);
    const finalPrice = Math.round(basePrice - discountAmount);

    return {
      discount: discountPercent,
      discountedPrice: finalPrice,
      originalPrice: basePrice,
      savedAmount: discountAmount,
    };
  }, [teamSize]);

  const handleStripeProduct = async (price: string, members?: number) => {
    if (pendingPrice) return;
    if (!user) {
      return router.push(`/login?price=${price}#auth-sign-up`);
    }
    setPendingPrice(price);
    try {
      const { sessionId } = await postData({
        url: '/api/create-checkout-session-product',
        data: {
          price: price,
          flow_data_type: members
            ? `team_size_${members}_discount_${discount}`
            : '',
        },
        token: '',
      });

      analytics.track('Headshots Purchase Initiated', {
        price: price,
        pricingType: pricingType,
        members: members || 1,
        discount: discount,
      });

      const stripe = await getStripe();
      if (!stripe)
        throw new Error(t('Unable to open checkout. Please try again.'));
      const result = await stripe.redirectToCheckout({ sessionId });
      if (result?.error) throw new Error(result.error.message);
    } catch (error) {
      return toast.error((error as Error)?.message);
    } finally {
      setPendingPrice(null);
    }
  };

  const togglePricingType = () => {
    setPricingType(pricingType === 'individual' ? 'team' : 'individual');
  };

  const handleTeamSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTeamSize(parseInt(e.target.value, 10));
  };

  if (appearance === 'studio') {
    return (
      <StudioPricing
        plans={individualPricingOptions}
        testimonials={testimonials}
        pricingType={pricingType}
        setPricingType={setPricingType}
        teamSize={teamSize}
        setTeamSize={setTeamSize}
        discount={discount}
        discountedPrice={discountedPrice}
        originalPrice={originalPrice}
        pendingPrice={pendingPrice}
        onPurchase={handleStripeProduct}
      />
    );
  }

  return (
    <section className='relative mx-auto bg-gradient-to-b from-white to-gray-50 py-16 dark:from-slate-800 dark:to-slate-900'>
      <div className='relative mx-auto max-w-6xl sm:flex sm:justify-center md:px-8 xl:px-0'>
        <div className='w-full'>
          <div
            id='pricing'
            className='mx-auto w-full max-w-6xl space-y-12 p-8 dark:text-slate-200 sm:rounded-xl'
          >
            <div className='text-center'>
              <h2 className='mb-4 text-4xl font-extrabold tracking-tight'>
                {t('Pricing')}
              </h2>
              <p className='mx-auto max-w-2xl text-gray-600 dark:text-gray-400'>
                {t('Choose the perfect plan for your needs')}
              </p>
            </div>

            {/* Pricing Toggle Switch */}
            <div className='mb-12 flex items-center justify-center'>
              <span
                className={clsxm(
                  'mr-3 text-sm font-medium transition-colors duration-200',
                  pricingType === 'individual'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400',
                )}
              >
                {t('Individual')}
              </span>
              <button
                onClick={togglePricingType}
                className='relative inline-flex h-7 w-14 items-center rounded-full bg-gray-200 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-gray-700'
              >
                <span
                  className={clsxm(
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300',
                    pricingType === 'team' ? 'translate-x-8' : 'translate-x-1',
                  )}
                />
              </button>
              <span
                className={clsxm(
                  'ml-3 text-sm font-medium transition-colors duration-200',
                  pricingType === 'team'
                    ? 'text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 dark:text-gray-400',
                )}
              >
                {t('Team')}
              </span>
            </div>

            {/* Team Size Slider - Only show when team pricing is selected */}
            {pricingType === 'team' && (
              <div className='mx-auto mb-12 max-w-md rounded-xl bg-white p-6 shadow-lg transition-all duration-300 dark:bg-slate-800'>
                <div className='mb-4 flex justify-between'>
                  <span className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                    Team Size:{' '}
                    <span className='font-bold text-primary-600'>
                      {teamSize} members
                    </span>
                  </span>
                </div>
                <input
                  type='range'
                  min='3'
                  max='50'
                  value={teamSize}
                  onChange={handleTeamSizeChange}
                  className='h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 accent-primary-600 dark:bg-gray-700'
                  style={{
                    background: `linear-gradient(to right, #4F46E5 0%, #4F46E5 ${
                      ((teamSize - 3) / 47) * 100
                    }%, #E5E7EB ${((teamSize - 3) / 47) * 100}%, #E5E7EB 100%)`,
                  }}
                />
                <div className='mt-2 flex justify-between text-xs text-gray-500 dark:text-gray-400'>
                  <span>3</span>
                  <span>25</span>
                  <span>50</span>
                </div>
              </div>
            )}

            {pricingType === 'individual' ? (
              <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
                {individualPricingOptions.map((option, index) => (
                  <div
                    key={index}
                    className={`relative flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-slate-800 ${
                      option.title === 'Professional'
                        ? 'ring-2 ring-primary-500 dark:ring-primary-400'
                        : ''
                    }`}
                  >
                    {option.title === 'Professional' && (
                      <div className='absolute -right-12 top-6 rotate-45 bg-primary-500 px-12 py-1 text-xs font-semibold uppercase tracking-wider text-white'>
                        {t('Popular')}
                      </div>
                    )}
                    <div className='flex-grow space-y-6'>
                      <h3 className='text-center text-2xl font-bold'>
                        {t(option.title)}
                      </h3>
                      <div className='text-center'>
                        <p className='text-4xl font-extrabold text-gray-900 dark:text-white'>
                          {t(option.price)}
                        </p>
                        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
                          {t('One-time payment')}
                        </p>
                      </div>
                      <p className='text-center text-sm text-gray-600 dark:text-slate-400'>
                        {t(option.description)}
                      </p>
                      <ul className='mb-8 space-y-4 pl-0'>
                        {option.features.map((feature, fIndex) => (
                          <li
                            key={fIndex}
                            className='flex items-center space-x-3'
                          >
                            <CheckIcon className='h-5 w-5 flex-shrink-0 text-primary-500' />
                            <span className='text-gray-700 dark:text-gray-300'>
                              {t(feature)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className='mt-6 text-center'>
                      <Button
                        onClick={() => handleStripeProduct(option.priceID)}
                        variant={
                          option.title === 'Professional'
                            ? 'primary'
                            : 'outline'
                        }
                        className={clsxm(
                          'w-full justify-center py-3 text-center transition-all duration-300',
                          option.title === 'Professional'
                            ? 'shadow-md hover:shadow-lg'
                            : '',
                        )}
                      >
                        {t(option.buttonText)}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className='grid grid-cols-1 gap-8 md:grid-cols-2'>
                <div className='rounded-2xl border border-primary-100 bg-gradient-to-br from-blue-50 to-white p-8 shadow-lg transition-all duration-300 dark:border-primary-900 dark:from-slate-800 dark:to-slate-900 md:col-span-2'>
                  <div className='mb-8 text-center'>
                    <div className='flex items-center justify-center'>
                      <p className='text-5xl font-extrabold text-primary-600 dark:text-primary-400'>
                        ${discountedPrice}
                      </p>
                      {discount > 0 && (
                        <span className='ml-3 text-xl text-gray-500 line-through dark:text-gray-400'>
                          ${originalPrice}
                        </span>
                      )}
                      <span
                        className={clsxm(
                          'ml-3 rounded-full px-3 py-1 text-sm font-medium',
                          discount > 0
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                        )}
                      >
                        {discount}% OFF
                      </span>
                    </div>
                    <p className='mt-2 text-sm text-gray-600 dark:text-slate-400'>
                      {discount > 0
                        ? t(`$29 per member with ${discount}% volume discount`)
                        : t('$29 per team member')}
                    </p>
                    <p className='mt-2 text-xs text-gray-500 dark:text-gray-400'>
                      {t('Team size will be fixed at checkout')}
                    </p>
                    {discount > 0 && (
                      <p className='mt-2 text-xs text-green-600 dark:text-green-400'>
                        {t('Discount will be applied automatically')}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={() =>
                      handleStripeProduct(HEADSHOTS_PRICES[1], teamSize)
                    }
                    variant='primary'
                    className={clsxm(
                      'mx-auto block w-full justify-center py-4 text-center text-base shadow-md transition-all duration-300 hover:shadow-lg md:w-1/2',
                    )}
                  >
                    {t(`Get Headshots for Your Team`)}
                  </Button>
                </div>
                <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-slate-800'>
                  <h3 className='mb-6 text-center text-2xl font-bold text-primary-600 dark:text-primary-400'>
                    {t('Your team gets:')}
                  </h3>
                  <ul className='space-y-5'>
                    {teamMemberBenefits.map((benefit, index) => (
                      <li key={index} className='flex items-start space-x-3'>
                        <CheckIcon className='mt-0.5 h-6 w-6 flex-shrink-0 text-primary-500' />
                        <span className='text-base text-gray-700 dark:text-gray-300'>
                          {t(benefit)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className='rounded-2xl border border-gray-200 bg-white p-8 shadow-lg transition-all duration-300 hover:shadow-xl dark:border-gray-700 dark:bg-slate-800'>
                  <h3 className='mb-6 text-center text-2xl font-bold text-primary-600 dark:text-primary-400'>
                    {t('Your team leader gets:')}
                  </h3>
                  <ul className='space-y-5'>
                    {teamLeaderBenefits.map((benefit, index) => (
                      <li key={index} className='flex items-start space-x-3'>
                        <CheckIcon className='mt-0.5 h-6 w-6 flex-shrink-0 text-primary-500' />
                        <span className='text-base text-gray-700 dark:text-gray-300'>
                          {t(benefit)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>

          <div className='mx-auto w-full max-w-6xl space-y-8 p-8 dark:text-slate-200 sm:rounded-lg'>
            <h3 className='mb-8 text-center text-2xl font-bold'>
              {t('What our customers say')}
            </h3>
            <div className='grid grid-cols-1 gap-8 md:grid-cols-3'>
              {testimonials.map((testimonial, index) => (
                <div
                  key={index}
                  className='flex flex-col items-center rounded-2xl bg-white p-6 shadow-lg transition-all duration-300 hover:shadow-xl dark:bg-slate-800'
                >
                  <Image
                    src={testimonial.image}
                    alt={testimonial.name}
                    width={100}
                    height={100}
                    className='rounded-full object-cover ring-2 ring-primary-100 dark:ring-primary-900'
                  />
                  <div className='mt-4 flex flex-col items-center text-center'>
                    <div className='mb-3 flex justify-center'>
                      {[...Array(testimonial.stars)].map((_, i) => (
                        <svg
                          key={i}
                          className='h-5 w-5 text-yellow-400'
                          fill='currentColor'
                          viewBox='0 0 20 20'
                          xmlns='http://www.w3.org/2000/svg'
                        >
                          <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z'></path>
                        </svg>
                      ))}
                    </div>
                    <p className='italic text-gray-700 dark:text-gray-300'>
                      {t(testimonial.quote)}
                    </p>
                    <p className='mt-4 font-bold'>{t(testimonial.name)}</p>
                    <p className='text-sm text-gray-600 dark:text-slate-400'>
                      {t(testimonial.title)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const individualPricingOptions = [
  {
    title: 'Basic',
    price: '$19',
    description:
      'Perfect for individuals looking to enhance their online presence.',
    features: ['3 hours turnaround time', '60 headshots', '6 unique styles'],
    buttonText: 'Get 60 Headshots in 3 Hours',
    bgColor: 'bg-white dark:bg-slate-700',
    priceID: HEADSHOTS_PRICES[0],
  },
  {
    title: 'Professional',
    price: '$29',
    description:
      'Ideal for professionals requiring frequent updates to their profiles.',
    features: ['2 hours turnaround time', '100 headshots', '15 unique styles'],
    buttonText: 'Get 100 Headshots in 2 Hours',
    bgColor: 'bg-blue-50 dark:bg-slate-800',
    badge: '100% Money Back Guarantee',
    priceID: HEADSHOTS_PRICES[1],
  },
  {
    title: 'Executive',
    price: '$49',
    description: 'The best value with unlimited possibilities.',
    features: ['1 hour turnaround time', '180 headshots', '25 unique styles'],
    buttonText: 'Get 180 Headshots in 1 Hour',
    bgColor: 'bg-white dark:bg-slate-700',
    priceID: HEADSHOTS_PRICES[2],
  },
];

const teamMemberBenefits = [
  '180 photos to choose from',
  '25 unique styles',
  'HD high quality',
  'Get it done right from home',
];

const teamLeaderBenefits = [
  'Save up to 10x',
  'Onboard new hires easily',
  'Easy to use admin dashboard',
  'Coordinate from one simple app',
  'Done in 1 hour instead of weeks',
];

const testimonials = [
  {
    name: 'Dawid Makowski',
    title: 'CTO, Co-Founder at Careera',
    quote:
      "The AI headshots generated were incredibly realistic and professional. Highly recommend it—you'll look better than your mirror ever told you.",
    image: '/images/headshots/testimonial/dawid.jpeg',
    stars: 5,
  },
  {
    name: 'Sylvia Chen',
    title: 'Founder',
    quote:
      'The AI-generated headshots exceeded my expectations. The quality and realism are outstanding.',
    image: '/images/headshots/testimonial/sylvia.png',
    stars: 4,
  },
  {
    name: 'Georgi Chelev',
    title: 'CTO & Partner at RaceFacer.com',
    quote:
      'The results blew me away. The variety of styles and the quick turnaround time make this service invaluable for professionals.',
    image: '/images/headshots/testimonial/georgi.jpeg',
    stars: 5,
  },
];
